import type { TabsProps } from 'antd';
import type { ReactNode } from 'react';
import { CouplingHistoryTable } from '../../../components/CouplingHistoryTable';
import { VehicleTyresTab } from '../../../../maintenance/features/tyres/public/VehicleTyresTab';
import { SPARE_TRAILER_TAB_LABELS, SPARE_TRAILER_TABS, type SpareTrailerTab } from '../../units/units.paths';
import type { useTrailerDetail } from '../hooks/use-trailer-detail';
import type { TrailerDetail } from '../trailers.api';
import { TrailerDetailsForm } from './TrailerDetailsForm';
import { TrailerDocuments } from './TrailerDocuments';
import { TrailerHistoryTab } from './TrailerHistoryTab';
import { TrailerOwnershipTab } from './TrailerOwnershipTab';

interface BuildTrailerDetailTabsArgs {
  trailer: TrailerDetail;
  canManage: boolean;
  canManageDocuments: boolean;
  canViewMaintenance: boolean;
  detail: ReturnType<typeof useTrailerDetail>;
  /** Bumped after a couple or uncouple, so the Truck history tab reloads. */
  couplingVersion: number;
}

/** The tabs of a spare trailer's page, in `SPARE_TRAILER_TABS` order; its Tyres tab needs `maintenance.view`. */
export function buildTrailerDetailTabs({
  trailer,
  canManage,
  canManageDocuments,
  canViewMaintenance,
  detail,
  couplingVersion,
}: BuildTrailerDetailTabsArgs): TabsProps['items'] {
  const content: Record<SpareTrailerTab, () => ReactNode> = {
    details: () => <TrailerDetailsForm trailer={trailer} canManage={canManage} error={detail.updateError} saving={detail.saving} onSave={detail.update} />,
    tyres: () => <VehicleTyresTab vehicleKind="trailer" vehicleId={trailer.id} />,
    documents: () => (
      <TrailerDocuments
        documents={trailer.documents}
        canManage={canManageDocuments}
        submitting={detail.documentSubmitting}
        onAdd={detail.addDocument}
        onOpen={detail.openDocument}
      />
    ),
    ownership: () => <TrailerOwnershipTab trailerId={trailer.id} trailerRegistration={trailer.registration} canManage={canManage} />,
    couplings: () => <CouplingHistoryTable key={couplingVersion} vehicleKind="trailer" vehicleId={trailer.id} />,
    history: () => <TrailerHistoryTab trailerId={trailer.id} />,
  };
  return SPARE_TRAILER_TABS.filter((tab) => canViewMaintenance || tab !== 'tyres').map((tab) => ({
    key: tab,
    label: SPARE_TRAILER_TAB_LABELS[tab],
    children: content[tab](),
  }));
}
